import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Settings,
  Image,
  Mail,
  Building,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, API_FOR_IMG } from "../App";

export default function AdminCMS() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [content, setContent] = useState({
    // Branding
    company_name: "ISP Manager",
    company_tagline: "Your Trusted Internet Service Provider",
    logo_url: "",
    favicon_url: "",

    // Landing Page Images
    hero_image_url: "",
    login_bg_image_url: "",
    about_image_url: "",
    soa_top_image_url: "",
    soa_gcash_image_url: "",

    // Hero Section
    hero_title: "",
    hero_subtitle: "",

    // About Section
    about_title: "",
    about_content: "",

    // Services
    service_1_title: "",
    service_1_desc: "",
    service_2_title: "",
    service_2_desc: "",
    service_3_title: "",
    service_3_desc: "",

    // Contact
    contact_email: "",
    contact_phone: "",
    contact_address: "",

    // GCash Payment Settings
    gcash_number: "",
    gcash_name: "",

    // Email Settings
    email_enabled: false,
    email_reminder_recipients: [],
    email_confirmation_enabled: true,
    payment_reminder_subject: "Payment Reminder - {company_name}",
    payment_reminder_template:
      "Dear {client_name},\n\nThis is a reminder that your payment of ₱{amount} is due on {due_date}.\n\nPlease settle your account to avoid service interruption.\n\nThank you,\n{company_name}",
    payment_confirmation_subject: "Payment Confirmed - {company_name}",
    payment_confirmation_template:
      "Dear {client_name},\n\nWe have received your payment of ₱{amount}.\nAccount Name: {client_name}\nAccount Number: {account_number}.\n\nThank you for your payment!\n\n{company_name}",
    general_email_subject: "ISP Manager Notification",
    domain_name: "yourdomain.com",
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cms/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setContent((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log("CMS settings not found, using defaults");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/cms/settings`, content, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("CMS settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, and WebP images are allowed");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Maximum file size is 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem("token");

      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          const response = await axios.post(
            `${API}/cms/upload-image`,
            {
              file_data: reader.result,
              filename: file.name,
              field: field,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          setContent((prev) => ({ ...prev, [field]: response.data.url }));
          toast.success("Image uploaded successfully");
        } catch (error) {
          toast.error("Failed to upload image");
        } finally {
          setUploadingImage(false);
        }
      };
    } catch (error) {
      toast.error("Failed to process image");
      setUploadingImage(false);
    }
  };

  // const handleDeleteImage = async (field) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     await axios.delete(`${API}/cms/delete-image`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //       data: { field: field },
  //     });
  //     setContent((prev) => ({ ...prev, [field]: "" }));
  //     toast.success("Image deleted successfully");
  //   } catch (error) {
  //     toast.error("Failed to delete image");
  //   }
  // };

  const addEmailRecipient = () => {
    setContent((prev) => ({
      ...prev,
      email_reminder_recipients: [
        ...(prev.email_reminder_recipients || []),
        "",
      ],
    }));
  };

  const updateEmailRecipient = (index, value) => {
    const updated = [...(content.email_reminder_recipients || [])];
    updated[index] = value;
    setContent((prev) => ({ ...prev, email_reminder_recipients: updated }));
  };

  const removeEmailRecipient = (index) => {
    const updated = [...(content.email_reminder_recipients || [])];
    updated.splice(index, 1);
    setContent((prev) => ({ ...prev, email_reminder_recipients: updated }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">
            Content Management System
          </h1>
          <p className="text-slate-600 mt-1">
            Manage your website content, branding, and email settings
          </p>
        </div>

        <form onSubmit={handleSave}>
          <Tabs defaultValue="branding" className="space-y-6">
            <TabsList className="grid grid-cols-5 w-full max-w-2xl">
              <TabsTrigger value="branding" className="gap-2">
                <Building className="w-4 h-4" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="images" className="gap-2">
                <Image className="w-4 h-4" />
                Images
              </TabsTrigger>
              <TabsTrigger value="content" className="gap-2">
                <Settings className="w-4 h-4" />
                Content
              </TabsTrigger>
              <TabsTrigger value="email" className="gap-2">
                <Mail className="w-4 h-4" />
                Email
              </TabsTrigger>
              {/* <TabsTrigger value="addfeature" className="gap-2">
                <Plus className="w-5 h-4" />
                Features
              </TabsTrigger> */}
            </TabsList>

            {/* Branding Tab */}
            <TabsContent value="branding">
              <Card>
                <CardHeader>
                  <CardTitle>Company Branding</CardTitle>
                  <CardDescription>
                    Customize your company name and branding elements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={content.company_name}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            company_name: e.target.value,
                          })
                        }
                        placeholder="ISP Manager"
                        data-testid="cms-company-name"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        This will be displayed in the header and emails
                      </p>
                    </div>
                    <div>
                      <Label>Company Tagline</Label>
                      <Input
                        value={content.company_tagline}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            company_tagline: e.target.value,
                          })
                        }
                        placeholder="Your Trusted Internet Service Provider"
                        data-testid="cms-company-tagline"
                      />
                    </div>
                    <div>
                      <Label>Domain Name</Label>
                      <Input
                        value={content.domain_name}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            domain_name: e.target.value,
                          })
                        }
                        placeholder="yourdomain.com"
                        data-testid="cms-domain-name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Logo</Label>
                      <div className="flex items-center gap-4 mt-2">
                        {content.logo_url && (
                          <img
                            src={
                              content.logo_url.startsWith("http")
                                ? content.logo_url
                                : `${API_FOR_IMG}${content.logo_url}`
                            }
                            alt="Logo"
                            className="h-12 object-contain border rounded"
                          />
                        )}
                        <div>
                          <Input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp"
                            onChange={(e) => handleImageUpload("logo_url", e)}
                            className="max-w-xs"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label>Favicon</Label>
                      <div className="flex items-center gap-4 mt-2">
                        {content.favicon_url && (
                          <img
                            src={
                              content.favicon_url.startsWith("http")
                                ? content.favicon_url
                                : `${API_FOR_IMG}${content.favicon_url}`
                            }
                            alt="Favicon"
                            className="h-8 w-8 object-contain border rounded"
                          />
                        )}
                        <div>
                          <Input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.ico"
                            onChange={(e) =>
                              handleImageUpload("favicon_url", e)
                            }
                            className="max-w-xs"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-4">
                      Contact Information
                    </h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          value={content.contact_email}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              contact_email: e.target.value,
                            })
                          }
                          placeholder="info@yourcompany.com"
                        />
                      </div>
                      <div>
                        <Label>Contact Phone</Label>
                        <Input
                          value={content.contact_phone}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              contact_phone: e.target.value,
                            })
                          }
                          placeholder="+63 912 345 6789"
                        />
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={content.contact_address}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              contact_address: e.target.value,
                            })
                          }
                          placeholder="123 Main St, City"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-4">
                      GCash Payment Settings
                    </h4>
                    <p className="text-sm text-slate-500 mb-4">
                      Configure GCash payment details for SOA PDF generation
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>GCash Number</Label>
                        <Input
                          value={content.gcash_number}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              gcash_number: e.target.value,
                            })
                          }
                          placeholder="09123456789"
                          data-testid="cms-gcash-number"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Will be displayed in SOA PDF with QR code
                        </p>
                      </div>
                      <div>
                        <Label>GCash Account Name</Label>
                        <Input
                          value={content.gcash_name}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              gcash_name: e.target.value,
                            })
                          }
                          placeholder="Juan Dela Cruz"
                          data-testid="cms-gcash-name"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Name registered with GCash account
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-4">
                      Social Media Accounts (Optional)
                    </h4>
                    <p className="text-sm text-slate-500 mb-4">
                      Configure social media links for your business
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Facebook Page Name</Label>
                        <Input
                          value={content.fbpage_name}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              fbpage_name: e.target.value,
                            })
                          }
                          placeholder="Your Facebook Page Name"
                          data-testid="cms-facebook-page-name"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Connected to chat widget on the website. Example:
                          "https://www.facebook.com/wifiipsagel/" gets
                          "wifiipsagel" as the page name.
                        </p>
                      </div>
                      {/* <div>
                        <Label>GCash Account Name</Label>
                        <Input
                          value={content.gcash_name}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              gcash_name: e.target.value,
                            })
                          }
                          placeholder="Juan Dela Cruz"
                          data-testid="cms-gcash-name"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Name registered with GCash account
                        </p>
                      </div> */}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Images Tab */}
            <TabsContent value="images">
              <Card>
                <CardHeader>
                  <CardTitle>Page Images</CardTitle>
                  <CardDescription>
                    Upload images for landing page and login page backgrounds
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div>
                    <Label className="text-base font-medium">
                      Hero Section Image
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Main banner image on the landing page
                    </p>
                    <div className="flex items-start gap-6">
                      <div
                        className="w-64 h-36 bg-slate-100 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer"
                        onClick={() =>
                          document.getElementById("hero-image-input").click()
                        }
                      >
                        {content.hero_image_url ? (
                          <img
                            src={
                              content.hero_image_url.startsWith("http")
                                ? content.hero_image_url
                                : `${API_FOR_IMG}${content.hero_image_url}`
                            }
                            alt="Hero"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          className="cursor-pointer"
                          id="hero-image-input"
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) =>
                            handleImageUpload("hero_image_url", e)
                          }
                          disabled={uploadingImage}
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          Recommended: 1920x600px, Max 5MB
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() =>
                          document.getElementById("hero-image-input").click()
                        }
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <Upload className="animate-spin w-4 h-4" />
                        ) : (
                          <span>Change Image</span>
                        )}
                      </Button>
                      {/* <Trash2 className="w-4 h-4" /> */}
                      {/* <Button
                        variant="destructive"
                        onClick={() => handleDeleteImage("hero_image_url")}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <Upload className="animate-spin w-4 h-4" />
                        ) : (
                          <span>Delete Image</span>
                        )}
                      </Button> */}
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <Label className="text-base font-medium">
                      Login Page Background
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Background image for the login page
                    </p>
                    <div className="flex items-start gap-6">
                      <div className="w-64 h-36 bg-slate-100 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 flex items-center justify-center">
                        {content.login_bg_image_url ? (
                          <img
                            src={
                              content.login_bg_image_url.startsWith("http")
                                ? content.login_bg_image_url
                                : `${API_FOR_IMG}${content.login_bg_image_url}`
                            }
                            alt="Login BG"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) =>
                            handleImageUpload("login_bg_image_url", e)
                          }
                          disabled={uploadingImage}
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          Recommended: 1920x1080px, Max 5MB
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <Label className="text-base font-medium">
                      About Section Image
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Image displayed in the about section
                    </p>
                    <div className="flex items-start gap-6">
                      <div className="w-64 h-36 bg-slate-100 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 flex items-center justify-center">
                        {content.about_image_url ? (
                          <img
                            src={
                              content.about_image_url.startsWith("http")
                                ? content.about_image_url
                                : `${API_FOR_IMG}${content.about_image_url}`
                            }
                            alt="About"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) =>
                            handleImageUpload("about_image_url", e)
                          }
                          disabled={uploadingImage}
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          Recommended: 800x600px, Max 5MB
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <Label className="text-base font-medium">
                      SOA Section Image
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Top image displayed in the SOA section
                    </p>
                    <div className="flex items-start gap-6">
                      <div className="w-64 h-36 bg-slate-100 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 flex items-center justify-center">
                        {content.soa_top_image_url ? (
                          <img
                            src={
                              content.soa_top_image_url.startsWith("http")
                                ? content.soa_top_image_url
                                : `${API_FOR_IMG}${content.soa_top_image_url}`
                            }
                            alt="About"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) =>
                            handleImageUpload("soa_top_image_url", e)
                          }
                          disabled={uploadingImage}
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          Recommended: 1561x234px, Max 2MB
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <Label className="text-base font-medium">
                      GCash Section Image
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      Footer image displayed in the GCash section
                    </p>
                    <div className="flex items-start gap-6">
                      <div className="w-64 h-36 bg-slate-100 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 flex items-center justify-center">
                        {content.soa_gcash_image_url ? (
                          <img
                            src={
                              content.soa_gcash_image_url.startsWith("http")
                                ? content.soa_gcash_image_url
                                : `${API_FOR_IMG}${content.soa_gcash_image_url}`
                            }
                            alt="About"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center text-slate-400">
                            <Image className="w-8 h-8 mx-auto mb-2" />
                            <span className="text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) =>
                            handleImageUpload("soa_gcash_image_url", e)
                          }
                          disabled={uploadingImage}
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          Recommended: 604x823px, Max 5MB
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content">
              <Card>
                <CardHeader>
                  <CardTitle>Landing Page Content</CardTitle>
                  <CardDescription>
                    Edit text content for your landing page
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">Hero Section</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Hero Title</Label>
                        <Input
                          value={content.hero_title}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              hero_title: e.target.value,
                            })
                          }
                          placeholder="Fast & Reliable Internet"
                        />
                      </div>
                      <div>
                        <Label>Hero Subtitle</Label>
                        <Input
                          value={content.hero_subtitle}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              hero_subtitle: e.target.value,
                            })
                          }
                          placeholder="Experience the best internet service"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900">
                      About Section
                    </h4>
                    <div>
                      <Label>About Title</Label>
                      <Input
                        value={content.about_title}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            about_title: e.target.value,
                          })
                        }
                        placeholder="About Us"
                      />
                    </div>
                    <div>
                      <Label>About Content</Label>
                      <Textarea
                        value={content.about_content}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            about_content: e.target.value,
                          })
                        }
                        rows={4}
                        placeholder="Tell your visitors about your company..."
                      />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900">Services</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Service 1 Title</Label>
                        <Input
                          value={content.service_1_title}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_1_title: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Service 1 Description</Label>
                        <Input
                          value={content.service_1_desc}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_1_desc: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Service 2 Title</Label>
                        <Input
                          value={content.service_2_title}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_2_title: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Service 2 Description</Label>
                        <Input
                          value={content.service_2_desc}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_2_desc: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Service 3 Title</Label>
                        <Input
                          value={content.service_3_title}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_3_title: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Service 3 Description</Label>
                        <Input
                          value={content.service_3_desc}
                          onChange={(e) =>
                            setContent({
                              ...content,
                              service_3_desc: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle>Email Settings</CardTitle>
                  <CardDescription>
                    Configure email notifications for payment reminders and
                    confirmations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="email_enabled"
                      checked={content.email_enabled}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          email_enabled: e.target.checked,
                        })
                      }
                      className="w-5 h-5"
                    />
                    <div>
                      <Label
                        htmlFor="email_enabled"
                        className="text-base font-medium cursor-pointer"
                      >
                        Enable Email Notifications
                      </Label>
                      <p className="text-sm text-slate-500">
                        Send payment reminders and confirmations to clients and
                        customer inquiries to admin/staff. <br /> Make sure to
                        add recipient emails below.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900">
                      General Email Subject
                    </h4>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={content.general_email_subject}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            general_email_subject: e.target.value,
                          })
                        }
                        placeholder="Payment Reminder - {company_name}"
                      />
                      {/* <p className="text-xs text-slate-500 mt-1">
                        Available variables: {"{company_name}"}
                      </p> */}
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-slate-900">
                        Email Recipients for Customer Inquiries
                      </h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addEmailRecipient}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Recipient
                      </Button>
                    </div>
                    <p className="text-sm text-slate-500">
                      Add admin/staff email addresses to receive copies of
                      customer inquiries in the website.
                    </p>

                    {(content.email_reminder_recipients || []).length === 0 ? (
                      <div className="text-center py-6 bg-slate-50 rounded-lg">
                        <Mail className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">
                          No recipients added yet
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(content.email_reminder_recipients || []).map(
                          (email, index) => (
                            <div key={index} className="flex items-center pb-2">
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) =>
                                  updateEmailRecipient(index, e.target.value)
                                }
                                placeholder="email@example.com"
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEmailRecipient(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>

                  {/* <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        id="email_confirmation"
                        checked={content.email_confirmation_enabled}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            email_confirmation_enabled: e.target.checked,
                          })
                        }
                        className="w-4 h-4"
                      />
                      <Label
                        htmlFor="email_confirmation"
                        className="cursor-pointer"
                      >
                        Send payment confirmation to clients
                      </Label>
                    </div>
                  </div> */}

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900">
                      Payment Reminder Template
                    </h4>
                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={content.payment_reminder_subject}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            payment_reminder_subject: e.target.value,
                          })
                        }
                        placeholder="Payment Reminder - {company_name}"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Available variables: {"{company_name}"}
                      </p>
                    </div>
                    <div>
                      <Label>Email Body</Label>
                      <Textarea
                        className="h-[200px]"
                        value={content.payment_reminder_template}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            payment_reminder_template: e.target.value,
                          })
                        }
                        rows={6}
                        placeholder="Enter email template..."
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Available variables:{" "}
                        {"{client_name}, {amount}, {due_date}, {company_name}"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900">
                      Payment Confirmation Template
                    </h4>
                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={content.payment_confirmation_subject}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            payment_confirmation_subject: e.target.value,
                          })
                        }
                        placeholder="Payment Confirmed - {company_name}"
                      />
                    </div>
                    <div>
                      <Label>Email Body</Label>
                      <Textarea
                        className="h-[200px]"
                        value={content.payment_confirmation_template}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            payment_confirmation_template: e.target.value,
                          })
                        }
                        rows={6}
                        placeholder="Enter email template..."
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Available variables:{" "}
                        {
                          "{client_name}, {amount}, {company_name}, {account_number}"
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Additional Feature Tab */}
            <TabsContent value="addfeature">
              <Card>
                <CardHeader>
                  <CardTitle>Additional Features</CardTitle>
                  <CardDescription>Enable new system features.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="email_enabled"
                      checked={content.email_enabled}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          email_enabled: e.target.checked,
                        })
                      }
                      className="w-5 h-5"
                    />
                    <div>
                      <Label
                        htmlFor="email_enabled"
                        className="text-base font-medium cursor-pointer"
                      >
                        New Subscribers Application
                      </Label>
                      <p className="text-sm text-slate-500 w-[90%]">
                        Manage new subscriber applications and streamline the
                        customer onboarding process. Receive notifications about
                        new applications, track application status, and send
                        automated email confirmations and updates to applicants
                        and admin/staff.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="email_enabled"
                      checked={content.email_enabled}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          email_enabled: e.target.checked,
                        })
                      }
                      className="w-5 h-5"
                    />
                    <div>
                      <Label
                        htmlFor="email_enabled"
                        className="text-base font-medium cursor-pointer"
                      >
                        Generated SOA
                      </Label>
                      <p className="text-sm text-slate-500 w-[90%]">
                        Automate the generation and distribution of Statements
                        of Account (SOA) for subscribers. Send billing
                        notifications, payment reminders, and account statements
                        through email to help customers stay informed about
                        their balances and payment due dates. Then now we can
                        show all the generated SOA using this feature.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="email_enabled"
                      checked={content.email_enabled}
                      onChange={(e) =>
                        setContent({
                          ...content,
                          email_enabled: e.target.checked,
                        })
                      }
                      className="w-5 h-5"
                    />
                    <div>
                      <Label
                        htmlFor="email_enabled"
                        className="text-base font-medium cursor-pointer"
                      >
                        Cash Flow
                      </Label>
                      <p className="text-sm text-slate-500">
                        Monitor and analyze the company's cash flow by tracking
                        collected payments, recorded expenses, and other
                        financial transactions. Generate financial summaries and
                        reports to help management evaluate revenue, expenses,
                        and overall business performance.
                      </p>
                    </div>
                  </div>

                  {/* <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        id="email_confirmation"
                        checked={content.email_confirmation_enabled}
                        onChange={(e) =>
                          setContent({
                            ...content,
                            email_confirmation_enabled: e.target.checked,
                          })
                        }
                        className="w-4 h-4"
                      />
                      <Label
                        htmlFor="email_confirmation"
                        className="cursor-pointer"
                      >
                        Send payment confirmation to clients
                      </Label>
                    </div>
                  </div> */}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-8 flex justify-end">
            <Button
              type="submit"
              disabled={saving || uploadingImage}
              className="bg-blue-600 hover:bg-blue-700 px-8"
              data-testid="cms-save-btn"
            >
              {saving ? "Saving..." : "Save All Changes"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
